import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  HStack,
  Text,
  useToast,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Badge,
  Flex,
} from "@chakra-ui/react";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { VotingPeriod, VotingPeriodProject } from "utils/apiService/PinkSale";
import useVotingPeriods from "./hooks/useVotingPeriods";
import CreateVotingPeriodModal from "components/modal/CreateVotingPeriodModal";
import queryKey from "configs/queryKey";

interface VotingPeriodTabProps {
  page: number;
  onPageChange: (page: number) => void;
}

const VotingPeriodTab = ({ page, onPageChange }: VotingPeriodTabProps) => {
  const history = useHistory();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isJsonModalOpen,
    onOpen: onJsonModalOpen,
    onClose: onJsonModalClose,
  } = useDisclosure();
  const {
    isOpen: isCreateModalOpen,
    onOpen: onCreateModalOpen,
    onClose: onCreateModalClose,
  } = useDisclosure();
  const [selectedPeriod, setSelectedPeriod] = useState<VotingPeriod | null>(
    null
  );
  const [selectedProject, setSelectedProject] =
    useState<VotingPeriodProject | null>(null);
  const limit = 20;

  const {
    data: votingPeriodData,
    isLoading,
    error,
  } = useVotingPeriods({
    page,
    limit,
  });

  const votingPeriods = votingPeriodData?.docs || [];
  const totalPages = votingPeriodData?.totalPages || 1;

  const handleViewJson = (period: VotingPeriod) => {
    setSelectedPeriod(period);
    onOpen();
  };

  const handleViewProjectJson = (project: VotingPeriodProject) => {
    setSelectedProject(project);
    onJsonModalOpen();
  };

  const truncateAddress = (
    address: string,
    startLength: number = 6,
    endLength: number = 4
  ) => {
    if (!address) return "";
    if (address.length <= startLength + endLength) return address;
    return `${address.slice(0, startLength)}...${address.slice(-endLength)}`;
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "ID copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const getStatusBadge = (period: VotingPeriod) => {
    if (period.isCompleted) {
      return <Badge colorScheme="green">Completed</Badge>;
    }
    if (period.isPaused) {
      return <Badge colorScheme="yellow">Paused</Badge>;
    }
    const now = new Date();
    const startTime = new Date(period.startTime);
    const endTime = new Date(period.endTime);

    if (now < startTime) {
      return <Badge colorScheme="blue">Upcoming</Badge>;
    }
    if (now >= startTime && now <= endTime) {
      return <Badge colorScheme="green">Active</Badge>;
    }
    return <Badge colorScheme="gray">Ended</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (error) {
    toast({
      title: "Error",
      description: "Failed to fetch voting periods",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries([queryKey.PINK_SALE_VOTING_PERIOD]);
    // Reset to first page after creating
    onPageChange(1);
  };

  return (
    <>
      <Flex justify="flex-end" mb={4}>
        <Button colorScheme="blue" onClick={onCreateModalOpen}>
          Create Voting Period
        </Button>
      </Flex>

      <Box overflowX="auto">
        {isLoading ? (
          <Center py={10}>
            <Spinner size="xl" />
          </Center>
        ) : !votingPeriods || votingPeriods.length === 0 ? (
          <Center py={10}>
            <Text>No voting periods found</Text>
          </Center>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Start Time</Th>
                <Th>End Time</Th>
                <Th>Status</Th>
                <Th>Created At</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {votingPeriods.map((period: VotingPeriod) => (
                <Tr key={period._id}>
                  <Td
                    cursor="copy"
                    onClick={() => handleCopy(period._id)}
                    _hover={{ color: "blue.500" }}
                  >
                    {period._id.slice(0, 8)}...
                  </Td>
                  <Td>{formatDate(period.startTime)}</Td>
                  <Td>{formatDate(period.endTime)}</Td>
                  <Td>{getStatusBadge(period)}</Td>
                  <Td>{formatDate(period.createdAt)}</Td>
                  <Td>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() =>
                          history.push(
                            `/admin/pinksale-voting-period/${period._id}`
                          )
                        }
                      >
                        View Details
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={() => handleViewJson(period)}
                      >
                        View JSON
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {!isLoading && votingPeriods && votingPeriods.length > 0 && (
        <Flex justify="space-between" align="center" mt={4}>
          <Text>
            Page {page} of {totalPages} (Total:{" "}
            {votingPeriodData?.totalDocs || 0})
          </Text>
          <HStack>
            <Button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              isDisabled={page === 1}
            >
              Previous
            </Button>
            <Button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              isDisabled={page === totalPages}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Voting Period JSON Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Voting Period JSON Data</ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              maxH="70vh"
              overflowY="auto"
            >
              <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                {selectedPeriod
                  ? JSON.stringify(selectedPeriod, null, 2)
                  : "No data selected"}
              </pre>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Project JSON Modal */}
      <Modal isOpen={isJsonModalOpen} onClose={onJsonModalClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Project JSON Data</ModalHeader>
          <ModalCloseButton />
          <ModalBody fontSize="sm">
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              maxH="70vh"
              overflowY="auto"
            >
              <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                {selectedProject
                  ? JSON.stringify(selectedProject, null, 2)
                  : "No data selected"}
              </pre>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onJsonModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Voting Period Modal */}
      <CreateVotingPeriodModal
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
};

export default VotingPeriodTab;
